/**
 * Μήνυμα κουζίνας από το κινητό (ρύθμιση kitchen_messages_enabled): «ΠΑΣΟ ΚΥΡΙΩΣ», «ΟΛΑ ΜΑΖΙ» …
 *
 * «send»: φεύγει αμέσως μόνο του (φύλλο τραπεζιού). «attach»: φεύγει μαζί με την παραγγελία
 * (καλάθι) και τυπώνεται στην κορυφή του δελτίου κουζίνας. Δεν είναι είδος — ούτε τιμή ούτε παραστατικό.
 */

import React, { useState } from 'react';
import { Check, Loader2, Plus, X } from 'lucide-react';
import {
  KITCHEN_MESSAGE_MAX_LENGTH,
  KITCHEN_MESSAGES_PER_SEND,
  isKitchenMessageSelected,
  toggleKitchenMessage,
  withDraftMessage,
} from '../../../utils/kitchenMessages';
import { Sheet, Button } from './ui';

interface KitchenMessageSheetProps {
  tableNumber: string | number;
  presets: string[];
  mode: 'send' | 'attach';
  initial?: string[];
  busy?: boolean;
  onConfirm: (messages: string[]) => void;
  onClose: () => void;
}

const KitchenMessageSheet: React.FC<KitchenMessageSheetProps> = ({
  tableNumber,
  presets,
  mode,
  initial = [],
  busy = false,
  onConfirm,
  onClose,
}) => {
  const [selected, setSelected] = useState<string[]>(initial);
  const [draft, setDraft] = useState('');

  const custom = selected.filter((m) => !presets.some((p) => isKitchenMessageSelected([p], m)));
  const full = selected.length >= KITCHEN_MESSAGES_PER_SEND;
  const finalMessages = withDraftMessage(selected, draft);

  const addDraft = () => {
    setSelected((prev) => withDraftMessage(prev, draft));
    setDraft('');
  };

  return (
    <Sheet
      title="Μήνυμα κουζίνας"
      subtitle={mode === 'attach' ? `Μαζί με την παραγγελία · Τραπέζι ${tableNumber}` : `Τραπέζι ${tableNumber}`}
      onClose={onClose}
      footer={
        <div className="pb-1">
          <Button
            tone="primary"
            full
            disabled={busy || (mode === 'send' && finalMessages.length === 0)}
            onClick={() => onConfirm(finalMessages)}
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            {mode === 'attach' ? 'Μαζί με την παραγγελία' : 'Αποστολή στην κουζίνα'}
          </Button>
        </div>
      }
    >
      <div className="p-3 space-y-3">
        {presets.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {presets.map((message) => {
              const on = isKitchenMessageSelected(selected, message);
              return (
                <button
                  key={message}
                  aria-pressed={on}
                  disabled={!on && full}
                  onClick={() => setSelected((prev) => toggleKitchenMessage(prev, message))}
                  className={`min-h-[52px] px-2 rounded-xl border-2 font-bold text-[15px] leading-tight break-words disabled:opacity-40 ${
                    on ? 'bg-teal-600 border-teal-600 text-white' : 'bg-surface-1 border-line text-t1 active:bg-surface-2'
                  }`}
                >
                  {message}
                </button>
              );
            })}
          </div>
        )}

        {custom.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {custom.map((message) => (
              <span key={message} className="inline-flex items-center gap-1 pl-3 rounded-xl bg-teal-600 text-white text-[15px] font-bold">
                {message}
                <button
                  onClick={() => setSelected((prev) => toggleKitchenMessage(prev, message))}
                  aria-label="Αφαίρεση"
                  className="w-[44px] h-[44px] flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input
            value={draft}
            maxLength={KITCHEN_MESSAGE_MAX_LENGTH}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
            disabled={full}
            placeholder="Άλλο μήνυμα"
            className="flex-1 min-w-0 h-[48px] px-3 rounded-xl bg-surface-2 dark:bg-surface-1 border border-line text-t1 text-base placeholder-t4 focus:outline-none focus:border-teal-500 disabled:opacity-50"
          />
          <Button tone="neutral" disabled={full || !draft.trim()} onClick={addDraft}>
            <Plus className="w-5 h-5" />
            Προσθήκη
          </Button>
        </div>

        <p className="text-[13px] text-t4 px-1">
          Έως {KITCHEN_MESSAGES_PER_SEND} μηνύματα. Τυπώνονται με μεγάλα γράμματα στην κουζίνα — δεν είναι είδος και δεν μπαίνουν σε παραστατικό.
        </p>
      </div>
    </Sheet>
  );
};

export default KitchenMessageSheet;
